/**
 * AllSourcesContext
 *
 * Provides caching for the All Sources page so navigating away (e.g. to a
 * source detail page) and back does not trigger a full reload. Mirrors the
 * JournalArticlesContext but is simpler: there are no collections, topics or
 * secondary-sidebar options. State is driven by the search term, the
 * source-type toolbar filter, sort, and pagination.
 */

import { createContext, useContext, useState, useCallback, useRef, ReactNode } from "react"
import { JournalArticle } from "@/services/journalArticlesService"
import { getAllSources } from "@/services/allSourcesService"
import { useAuth } from "@/hooks/useAuth"
import { ViewMode } from "@/components/shared/TableViewSelector"
import { getColumnGroupsForViewMode } from "@/components/all-sources/columns"

/** Default page size for all-sources pagination. */
const DEFAULT_PAGE_SIZE = 50

/** Context value shape with state and actions for the All Sources page. */
interface AllSourcesContextValue {
  /** The list of sources on the current page. */
  sources: JournalArticle[]
  /**
   * Current page index (0-based). Kept purely for display ("page N") and URL
   * sync; keyset pagination has no random access, so this cannot be set to an
   * arbitrary value — it only advances via fetchNext / retreats via fetchPrev.
   */
  pageIndex: number
  /** Number of items per page. */
  pageSize: number
  /** Whether a further page exists after the current one. */
  hasMore: boolean
  /**
   * Running count of sources loaded up to and including the current page. Used
   * for the "showing N sources" label in place of an exact total (unavailable
   * with keyset pagination).
   */
  loadedCount: number
  /** Current search query string. */
  searchQuery: string
  /**
   * Active source-type filter: a sourceTypeLabelMap key (e.g. "report"), or
   * null for "all source types". Single-valued because the backend only
   * supports one sourceType per query.
   */
  sourceType: string | null
  /** Current view mode (compact, full). */
  viewMode: ViewMode
  /** Set of visible column keys. */
  visibleColumns: Set<string>
  /** Whether data is currently being fetched. */
  loading: boolean
  /** Error message if the last fetch failed. */
  error: string | null
  /** Timestamp of the last successful fetch (for cache invalidation). */
  lastFetchTime: number | null
  /** Currently selected source (for side panel). */
  selectedSource: JournalArticle | null
  /** ID of the currently selected source. */
  selectedSourceId: number | null
  /**
   * Fetch the first page with the current filters, resetting all pagination
   * state (cursor stack, page index, counts). Call on mount and whenever a
   * filter, search, or page size changes.
   */
  fetchFirstPage: () => Promise<void>
  /** Advance to and fetch the next page. No-op when hasMore is false. */
  fetchNextPage: () => Promise<void>
  /** Retreat to and fetch the previous page. No-op on the first page. */
  fetchPreviousPage: () => Promise<void>
  /** Set the page size. */
  setPageSize: (size: number) => void
  /** Set the search query. */
  setSearchQuery: (query: string) => void
  /** Set the active source-type filter (key, or null for all types). */
  setSourceType: (type: string | null) => void
  /** Set the view mode. */
  setViewMode: (mode: ViewMode) => void
  /** Set the visible columns. */
  setVisibleColumns: (columns: Set<string>) => void
  /** Select a source (shows side panel). */
  selectSource: (source: JournalArticle) => void
  /** Clear the selected source (closes side panel). */
  clearSelectedSource: () => void
}

const AllSourcesContext = createContext<AllSourcesContextValue | null>(null)

/** Props for the AllSourcesProvider component. */
interface AllSourcesProviderProps {
  /** Child components that can access the context. */
  children: ReactNode
}

/**
 * Provider component that manages All Sources state and caching.
 *
 * @param props - Provider props including children.
 * @returns The provider component wrapping children.
 */
export function AllSourcesProvider({ children }: AllSourcesProviderProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  // Core data state
  const [sources, setSources] = useState<JournalArticle[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null)

  // Pagination state (keyset / cursor-based).
  // pageIndex is a display-only counter; real navigation is driven by the
  // cursor stack. cursorStackRef holds the cursor used to fetch each page
  // visited so far: index i is the `cursor` argument that produced page i
  // (index 0 is null — the first page). nextCursorRef holds the cursor to fetch
  // the page AFTER the current one, as returned by the last response.
  const [pageIndex, setPageIndex] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
  const [hasMore, setHasMore] = useState<boolean>(false)
  const [loadedCount, setLoadedCount] = useState<number>(0)
  const cursorStackRef = useRef<(string | null)[]>([null])
  const nextCursorRef = useRef<string | null>(null)

  // Filter and view state. sourceType is the single active source-type filter
  // (a sourceTypeLabelMap key such as "report"), or null for "all types". The
  // backend only supports one type per query, so this is single-valued rather
  // than a multi-select filter map.
  const [searchQuery, setSearchQueryState] = useState<string>("")
  const [sourceType, setSourceTypeState] = useState<string | null>(null)
  const [viewMode, setViewModeState] = useState<ViewMode>("compact")
  const [visibleColumns, setVisibleColumnsState] = useState<Set<string>>(
    new Set(getColumnGroupsForViewMode("compact").flatMap((g) => g.keys))
  )

  // Selection state
  const [selectedSource, setSelectedSource] = useState<JournalArticle | null>(null)
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null)

  // Fetch ID ref to discard stale responses
  const fetchIdRef = useRef<number>(0)

  /**
   * Fetch a single page of sources from the API for the given cursor and update
   * state. Shared by fetchFirstPage / fetchNextPage / fetchPreviousPage.
   *
   * @param cursor - The pagination cursor to fetch (null for the first page).
   * @param displayIndex - The 0-based page index this fetch corresponds to,
   *   used for the display counter and the running loaded count.
   */
  const fetchForCursor = useCallback(
    async (cursor: string | null, displayIndex: number): Promise<void> => {
      const currentFetchId = ++fetchIdRef.current

      setLoading(true)
      setError(null)

      try {
        const token = await getAccessToken()

        const data = await getAllSources(token, cursor, pageSize, {
          sourceType: sourceType ?? undefined,
          searchTerm: searchQuery || undefined,
        })

        // Discard stale responses (filters/search/page may have changed).
        if (currentFetchId !== fetchIdRef.current) {
          return
        }

        setSources(data.items)
        setHasMore(data.hasMore)
        nextCursorRef.current = data.nextCursor
        setPageIndex(displayIndex)
        // Running count of items seen through this page. For a full page this is
        // (pages so far * pageSize); the final (possibly short) page adds only
        // its own length.
        setLoadedCount(displayIndex * pageSize + data.items.length)
        setLastFetchTime(Date.now())

        // If a selected source ID exists, try to resolve it from the loaded data
        if (selectedSourceId !== null) {
          const match = data.items.find((s) => s.sourceId === selectedSourceId)
          setSelectedSource(match ?? null)
        }
      } catch (err) {
        if (currentFetchId !== fetchIdRef.current) {
          return
        }
        setError(err instanceof Error ? err.message : "Failed to load sources")
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setLoading(false)
        }
      }
    },
    [getAccessToken, searchQuery, pageSize, sourceType, selectedSourceId]
  )

  /**
   * Fetch the first page, resetting all cursor pagination state. Call on mount
   * and whenever a filter, search term, or page size changes.
   */
  const fetchFirstPage = useCallback(async (): Promise<void> => {
    cursorStackRef.current = [null]
    nextCursorRef.current = null
    await fetchForCursor(null, 0)
  }, [fetchForCursor])

  /**
   * Advance to and fetch the next page by following the cursor returned with
   * the current page. No-op when there is no next page.
   */
  const fetchNextPage = useCallback(async (): Promise<void> => {
    const next = nextCursorRef.current
    if (next === null) return
    cursorStackRef.current = [...cursorStackRef.current, next]
    await fetchForCursor(next, cursorStackRef.current.length - 1)
  }, [fetchForCursor])

  /**
   * Retreat to and fetch the previous page using the remembered cursor stack.
   * No-op on the first page.
   */
  const fetchPreviousPage = useCallback(async (): Promise<void> => {
    if (cursorStackRef.current.length <= 1) return
    // Drop the current page's cursor; the new last entry is the previous page.
    const stack = cursorStackRef.current.slice(0, -1)
    cursorStackRef.current = stack
    const prevCursor = stack[stack.length - 1]
    await fetchForCursor(prevCursor, stack.length - 1)
  }, [fetchForCursor])

  /**
   * Set the search query. Resetting to the first page is handled by the
   * interface's change-detection effect, which calls fetchFirstPage.
   *
   * @param query - The new search query.
   */
  const setSearchQuery = useCallback((query: string): void => {
    setSearchQueryState(query)
    setSelectedSource(null)
    setSelectedSourceId(null)
  }, [])

  /**
   * Set the active source-type filter (a sourceTypeLabelMap key, or null for
   * "all types"). Resetting to the first page is handled by the interface's
   * change-detection effect, which calls fetchFirstPage.
   *
   * @param type - The source-type key to filter by, or null to clear.
   */
  const setSourceType = useCallback((type: string | null): void => {
    setSourceTypeState(type)
    setSelectedSource(null)
    setSelectedSourceId(null)
  }, [])

  /**
   * Set the view mode.
   *
   * @param mode - The new view mode.
   */
  const setViewMode = useCallback((mode: ViewMode): void => {
    setViewModeState(mode)
  }, [])

  /**
   * Set the visible columns.
   *
   * @param columns - The new set of visible column keys.
   */
  const setVisibleColumns = useCallback((columns: Set<string>): void => {
    setVisibleColumnsState(columns)
  }, [])

  /**
   * Select a source to view in the side panel.
   *
   * @param source - The source to select.
   */
  const selectSource = useCallback((source: JournalArticle): void => {
    setSelectedSource(source)
    setSelectedSourceId(source.sourceId)
  }, [])

  /**
   * Clear the selected source (closes the side panel).
   */
  const clearSelectedSource = useCallback((): void => {
    setSelectedSource(null)
    setSelectedSourceId(null)
  }, [])

  const value: AllSourcesContextValue = {
    sources,
    pageIndex,
    pageSize,
    hasMore,
    loadedCount,
    searchQuery,
    sourceType,
    viewMode,
    visibleColumns,
    loading,
    error,
    lastFetchTime,
    selectedSource,
    selectedSourceId,
    fetchFirstPage,
    fetchNextPage,
    fetchPreviousPage,
    setPageSize,
    setSearchQuery,
    setSourceType,
    setViewMode,
    setVisibleColumns,
    selectSource,
    clearSelectedSource,
  }

  return <AllSourcesContext.Provider value={value}>{children}</AllSourcesContext.Provider>
}

/**
 * Hook to access the All Sources context.
 *
 * @returns The All Sources context value.
 * @throws Error if used outside of an AllSourcesProvider.
 */
export function useAllSources(): AllSourcesContextValue {
  const context = useContext(AllSourcesContext)
  if (!context) {
    throw new Error("useAllSources must be used within an AllSourcesProvider")
  }
  return context
}
